import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { AutoComplete } from '@domgen/shared/enums/autoComplete';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '../assets/icons/ArrowLeft';
import { CircleCheck } from '../assets/icons/CircleCheck';
import { WashingMachineSmall } from '../assets/icons/WashingMachineSmall';
import { PageBackgroundWrapper } from '../components/PageBackgroundWrapper';
import { FormKey } from '../enums/formKey';
import { StepKey } from '../enums/stepKey';
import { StoreEvent } from '../enums/storeEvent';
import { useFormValidation } from '../hooks/useFormValidation';
import { capturePostHogEvent } from '../services/posthog/capture';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { store } from '../store/store';
import { formValidators } from '../utils/form/validators';
import { getSystemCode } from '../utils/getSystemCode';
import { formApplianceName } from '../utils/store/formApplianceName';
import { formCategoryName } from '../utils/store/formCategoryName';
import { capitalizeFirstLetter } from '../utils/string/capitalize';

const styles = {
  descriptionList: 'flex flex-col gap-2 my-2',
  descriptionListItemTitle: 'font-bold',
  descriptionListItem:
    'inline-flex justify-start items-center gap-1 mb-2 bg-neutral-50 p-1 rounded-(--border-radius) border-neutral-100',
};

type PricePageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function PricePage({ stepNumber, totalSteps }: PricePageProps) {
  const { t } = useTranslation(['purchasePricePage', 'common']);
  const formData = useApplianceFormData();
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const loadedFromParams =
    urlParams?.matchedAppliance &&
    urlParams?.mn === formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER];
  const VITE_CLIENT_FRIENDLY_NAME = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const applianceCategory = formCategoryName.get();
  const applianceName = formApplianceName.get();
  const [isFocused, setIsFocused] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'purchase-price',
    });
  }, []);

  const applianceDataFromParams = (
    <dl className={styles.descriptionList}>
      <dt className={styles.descriptionListItemTitle}>
        {capitalizeFirstLetter(t('applianceLabel'))}
      </dt>
      <dd className={styles.descriptionListItem}>
        <WashingMachineSmall />
        {VITE_CLIENT_FRIENDLY_NAME} {applianceName}
      </dd>
    </dl>
  );

  // Only display appliance category if the appliance type from the url param
  // matches the appliance type in the form in order to avoid displaying incorrect data
  // if the user has changed it in a previous step.
  const displayParamLoadedApplianceCategory = loadedFromParams ? applianceDataFromParams : null;

  const title = loadedFromParams ? t('headingWithApplianceDetails') : t('heading');

  const { formValues, errors, handleChange, validateAll } = useFormValidation(
    {
      [FormKey.PURCHASE_PRICE]: formData[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE] || '',
    },
    {
      [FormKey.PURCHASE_PRICE]: [
        formValidators.required(t('errors.required')),
        formValidators.numeric(t('errors.invalid')),
        formValidators.minNumber(0.01, t('errors.min')),
        formValidators.maxNumber(1000000, t('errors.max')),
      ],
    },
  );
  const handleContinue = () => {
    const isValid = validateAll();

    gaEvent({
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: isValid ? 'success' : 'failed',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-price',
      regSystemCode: systemCode,
    });

    capturePostHogEvent(posthog, 'price-continue-cta-clicked', store.get());

    if (!isValid) {
      return;
    }

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.PURCHASE_PRICE]: {
          [FormKey.PURCHASE_PRICE]: formValues[FormKey.PURCHASE_PRICE],
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-price',
    });
    capturePostHogEvent(posthog, 'price-back-clicked', store.get());
    store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.PURCHASE_PRICE } });
  };

  const handleBlur = () => {
    setIsFocused(false);
    const value = formValues.purchasePrice;

    if (!value) {
      return;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return;
    }

    // Reject negative values for purchase price instead of formatting them
    if (numericValue < 0) {
      handleChange({ target: { name: 'purchasePrice', value: '' } });
      return;
    }

    const formatted = numericValue.toFixed(2);
    handleChange({ target: { name: 'purchasePrice', value: formatted } });

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'purchasePrice',
      eventLabel: formatted,
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'purchase-price',
      regSystemCode: systemCode,
    });
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <PageBackgroundWrapper>
      <ProgressBars total={totalSteps} current={stepNumber} />

      <h1 className="my-(--spacing-space-400) text-h3">{title}</h1>
      <p className="mb-(--spacing-space-400)">{t('priceDescription')}</p>

      {displayParamLoadedApplianceCategory}

      <div className="flex flex-col gap-2">
        <div className="[&_input]:rounded-radius-100">
          <Input
            label={t('inputLabel')}
            placeholder={isFocused ? '' : t('placeholder')}
            type="number"
            leftIcon="$"
            name="purchasePrice"
            value={formValues.purchasePrice}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            hasError={errors['purchasePrice']}
            rightIcon={
              formValues.purchasePrice && !errors.purchasePrice ? <CircleCheck /> : undefined
            }
            hasSuccess={formValues.purchasePrice !== '' && !errors.purchasePrice}
            autoComplete={AutoComplete.OFF}
          />
        </div>
      </div>

      <div className="flex flex-col w-full mt-4 items-center md:flex-row-reverse md:justify-between">
        <div className="w-full md:w-fit mb-4">
          <Button onClick={handleContinue}>{t('common:continue')}</Button>
        </div>
        <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
          {t('common:back')}
        </TextLink>
      </div>
    </PageBackgroundWrapper>
  );
}
